import React, { CSSProperties, memo, MouseEventHandler, ComponentProps, Suspense, useCallback, useMemo } from 'react';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import Workspace from '../Workspace';
import { BasicHeader } from '../../actions/BasicHeader';
import FluentModal from '../modals/FluentModal';
import { CustomerGlobalContactLookup } from '../modals/CustomerGlobalContactLookup';
import { IFilterInput } from '../../graphql/schema/p4types';
import { DocumentNode } from '@apollo/client';
import { makeStyles } from '@fluentui/react-components';

const CustomerWorkspace = React.lazy(() => import('../../views/customer/CustomerWorkspace'));
const CustomerForm = React.lazy(() => import('../../views/customer/CustomerForm'));

const ProductWorkspace = React.lazy(() => import('../../views/product/ProductWorkspace'));
const ProductForm = React.lazy(() => import('../../views/product/ProductForm'));

const NominalWorkspace = React.lazy(() => import('../../views/nominal/NominalWorkspace'));
const NominalForm = React.lazy(() => import('../../views/nominal/NominalForm'));

const SupplierWorkspace = React.lazy(() => import('../../views/supplier/SupplierWorkspace'));
const SupplierForm = React.lazy(() => import('../../views/supplier/SupplierForm'));

const SalesProductLookup = React.lazy(() => import('../../views/product/SalesProductLookup'));

const StockLotLookupModal = React.lazy(() => import('../../views/product/StockLotLookupModal'));

type ISelectModalName =
  | 'CustomerWorkspace'
  | 'NominalWorkspace'
  | 'SupplierWorkspace'
  | 'ProductWorkspace'
  | 'StockLotLookupModal'
  | 'SelectModal'
  | 'SalesProductLookup'
  | 'GlobalContactLookup';

type IInnerSelectModalProps = {
  selectModalUpdateOpen: (open: boolean) => void;
  updateCellState: (options: { modalOpen?: boolean }) => void;
  setModalOpen: (open: boolean) => void;
  selectValue: any;
  focusOnInput: () => void;
  modalProps: any;
  selectModal: ISelectModalName;
  selectModalData?: () => any;
  activeTabId: string;
  predefinedFilters: Array<IFilterInput>;
  query: DocumentNode;
  labelKey: string;
  modalOpen: boolean;
  options?: Array<any>;
};

const InnerSelectContent = memo(({ modalProps, selectModal }: { modalProps: any; selectModal: ISelectModalName }) => {
  switch (selectModal) {
    case 'CustomerWorkspace':
      return (
        <CustomerWorkspace {...modalProps} className={'ignore-react-onclickoutside'} modalContentLink={CustomerForm} />
      );
    case 'NominalWorkspace':
      return (
        <NominalWorkspace {...modalProps} className={'ignore-react-onclickoutside'} modalContentLink={NominalForm} />
      );
    case 'ProductWorkspace':
      return (
        <ProductWorkspace {...modalProps} className={'ignore-react-onclickoutside'} modalContentLink={ProductForm} />
      );
    case 'SupplierWorkspace':
      return (
        <SupplierWorkspace {...modalProps} className={'ignore-react-onclickoutside'} modalContentLink={SupplierForm} />
      );
    case 'StockLotLookupModal':
      return <StockLotLookupModal {...modalProps} className={'ignore-react-onclickoutside'} />;
    default:
      return <DropdownWorkspace {...modalProps} className={'ignore-react-onclickoutside'} />;
  }
});

const useSalesProductLookupStyles = makeStyles({
  container: {
    minHeight: '96vh',
    maxHeight: '96vh',
    width: '90vw',
    overflowY: 'clip',
  },
});

const SalesProductLookupModal = ({
  modalOpen,
  hideModal,
  salesProductLookupProps,
  selectModalData,
}: {
  modalOpen: boolean;
  hideModal: (shouldCellStateUpdate?: boolean) => void;
  salesProductLookupProps: Partial<ComponentProps<typeof SalesProductLookup>>;
  selectModalData: ComponentProps<typeof SalesProductLookup>['supportingData'];
}) => {
  const styles = useSalesProductLookupStyles();

  return (
    <FluentModal isOpen={modalOpen} onDismiss={(ev) => hideModal(true)} scrollableContentClassName={styles.container}>
      <BasicHeader>Product Lookup</BasicHeader>
      <SalesProductLookup
        {...salesProductLookupProps}
        hideModal={() => hideModal(true)}
        modalContentLink={ProductForm}
        supportingData={selectModalData}
      />
    </FluentModal>
  );
};

export const InnerSelectModal = ({
  selectModalUpdateOpen,
  updateCellState,
  setModalOpen,
  selectValue,
  focusOnInput,
  modalProps: modalPropsFromProps,
  selectModal,
  activeTabId,
  predefinedFilters,
  query,
  labelKey,
  modalOpen,
  selectModalData,
  options,
  entity,
}: IInnerSelectModalProps) => {
  const updateModalOpen = useCallback(
    (newState, shouldCellStateUpdate = true) => {
      if (selectModalUpdateOpen) {
        selectModalUpdateOpen(newState);
      }
      if (shouldCellStateUpdate && updateCellState) {
        updateCellState({ modalOpen: newState });
      }
      setModalOpen(newState);
    },
    [updateCellState, selectModalUpdateOpen, setModalOpen]
  );

  const hideModal = useCallback(
    (shouldCellStateUpdate = true) => {
      updateModalOpen(false, shouldCellStateUpdate);
    },
    [updateModalOpen]
  );

  const onRowClick = useCallback(
    (rowData) => {
      if (rowData) {
        selectValue(rowData);
        focusOnInput();
        hideModal(false); //Don't update cell state or it will (potentially) unmount the modal
        // before the change can finish trickling up the state tree.
      }
    },
    [focusOnInput, hideModal, selectValue]
  );

  const modalProps = useMemo(() => {
    let newProps;
    if (typeof modalPropsFromProps === 'function') {
      newProps = Object.assign({}, modalPropsFromProps(), { key: 'modal' });
    } else {
      newProps = Object.assign({}, modalPropsFromProps, { key: 'modal' });
    }

    newProps.onClose = hideModal;
    newProps.onBack = hideModal;
    newProps.lookupView = true;
    newProps.embeddedView = true;
    newProps.onRowClick = onRowClick;
    newProps.backButtonRequired = true;
    newProps.viewScreenName = selectModal;
    newProps.noEdit = true;
    newProps.selectModalUpdateOpen = selectModalUpdateOpen;
    newProps.activeTabId = activeTabId;
    newProps.predefinedFilters = predefinedFilters;
    newProps.query = query;
    newProps.labelKey = labelKey;
    newProps.options = options;
    newProps.entity = entity;

    return newProps;
  }, [
    activeTabId,
    entity,
    hideModal,
    labelKey,
    modalPropsFromProps,
    onRowClick,
    options,
    predefinedFilters,
    query,
    selectModal,
    selectModalUpdateOpen,
  ]);

  if (selectModal === 'SalesProductLookup') {
    //Present SalesProductLookup in a fluent-ui modal to improve compatability with other modals in P4, such as CherryPickModal.
    return (
      <SalesProductLookupModal
        modalOpen={modalOpen}
        hideModal={hideModal}
        salesProductLookupProps={modalProps}
        selectModalData={selectModalData}
      />
    );
  } else if (selectModal === 'GlobalContactLookup') {
    return (
      <CustomerGlobalContactLookup modalOpen={modalOpen} hideModal={hideModal} onRowClick={modalProps.onRowClick} />
    );
  } else if (selectModal === 'StockLotLookupModal') {
    return <StockLotLookupModal {...modalProps} isOpen={modalOpen} onClose={hideModal} />;
  }

  //TODO - Prefer to have children do the 'padding: '1em'' part where appropriate, but to maintain consistency/appearnce with previous approach, put it here for the moment.
  return (
    <FluentModal onDismiss={hideModal} isOpen={modalOpen}>
      <div style={{ height: '90vh', width: '80vw', position: 'relative', padding: '1em' }}>
        <Suspense fallback={<ProgressIndicator />}>
          <InnerSelectContent modalProps={modalProps} selectModal={selectModal} />
        </Suspense>
      </div>
    </FluentModal>
  );
};

export const DropdownWorkspace = (props: any) => {
  const workspaceProps = useWorkspace(
    { viewScreenName: 'dropdownWorkspace' },
    props.entity ?? {
      workspace: props.query,
      type: 'PricingAttributeReference',
    },
    { columns: [props.labelKey], filters: props.predefinedFilters, initialSortColumn: props.labelKey }
  );

  return (
    <Workspace
      {...props}
      {...workspaceProps}
      ignoreContainerWidth={true}
      defaultQuery={props.query}
      title={'Expanded Dropdown Lookup'}
    />
  );
};

//Exported for re-use so custom "SelectModal"-alike implementations can share identical icon styling.
export const SearchSuffix = ({ onClick, style }: { onClick: MouseEventHandler; style?: CSSProperties }) => {
  const onMouseDown: MouseEventHandler = (event) => {
    if (event.type === 'mousedown' && event.button !== 0) {
      return;
    }
    // prevent default event handlers
    event.stopPropagation();
    event.preventDefault();
    if (onClick) onClick(event);
  };

  return (
    <span onMouseDown={onMouseDown} style={{ ...(style ?? {}), cursor: 'pointer' }}>
      <i className="iconSelect">search</i>
    </span>
  );
};

export default (props: IInnerSelectModalProps & { noIcon: boolean; onClick: (event: any) => void }) => {
  const { modalOpen, setModalOpen, noIcon, onClick } = props;

  const updateModalOpen = (newState: boolean, shouldCellStateUpdate = true) => {
    if (props.selectModalUpdateOpen) {
      props.selectModalUpdateOpen(newState);
    }
    if (shouldCellStateUpdate && props.updateCellState) {
      props.updateCellState({ modalOpen: newState });
    }
    setModalOpen(newState);
  };

  return (
    <span>
      {noIcon ? null : (
        <SearchSuffix
          onClick={(ev) => {
            onClick(ev);
            updateModalOpen(true);
          }}
        />
      )}
      {modalOpen ? (
        <InnerSelectModal key={'inner-select'} {...props} modalOpen={modalOpen} setModalOpen={setModalOpen} />
      ) : null}
    </span>
  );
};
